import { Affiliate, AffiliateRepresentation } from '@/interfaces/affiliates';
import { BloodType } from '@/interfaces/person';
import { PaginatedResponse } from '@/interfaces/response';
import { ApprovedFileAmount, CurrentUser, User, UserEmployeeResponse, UserErrorDetail, UserPKIsResponse } from '@/interfaces/user';
import { setPermissions } from './login.service';
import { AxiosPromise, AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios';
import request from '@/umi-request';

export async function queryCurrent() {
  return request({
    url: 'users/me/'
  }).then((response : AxiosResponse<CurrentUser>) => {
    const me = response.data;

    const { permissions } = me;

    if (me.is_employee) {
      permissions.push('is_employee');
    }

    if (me.is_admin) {
      permissions.push('is_admin');
    }

    if (me.affiliate_id) {
      permissions.push('is_affiliate');
    }

    setPermissions(permissions);

    return me;
  });
}

export async function changeUserPassword(data: {}) {
  return request({
    url: 'users/change-password/', 
    method: 'PUT', 
    data
  }) as AxiosPromise<CurrentUser>;
}

export async function updateMyUserData(data: {}) {
  return request({
    url: 'users/me/',  
    method: 'PUT', 
    data
  }) as AxiosPromise<CurrentUser>;
}

export async function updateMyFirstAccess() {
  return request({
    url: 'users/me/first-access/', 
    method: 'PUT', 
  }) as AxiosPromise<CurrentUser>;
}

export async function updateUserData(userID: number, data: {}) {
  return request({
    url: `users/${userID}/edit/`,
    method: 'PUT', 
    data
  }) as AxiosPromise<CurrentUser>;
}

export async function updateUserEmail(data: {}) {
  return request({
    url: 'users/me/',  
    method: 'PUT', 
    data
  }) as AxiosPromise<CurrentUser>;
}

export async function getMyAffiliates() {
  return request({
    url: 'users/me/affiliates/',
    method: 'GET',
  }) as AxiosPromise<Affiliate[]>
}


export async function getUsers(params: {}) {
  return request({
    url: 'admin/users/',
    method: 'GET',
    params
  }) as AxiosPromise<PaginatedResponse<User>>;
}

export async function getUser(id: number) {
  return request({
    url: `admin/users/${id}/`,
    method: 'GET'
  }) as AxiosPromise<User>;
}

export async function existsUser(params: any) {
  return getUsers(params).then((response) => response.data.count > 0);
}

export async function getRepresentations(id: number) {
  return request({
    url: `users/${id}/representations/`,
    method: 'GET'
  }) as AxiosPromise<PaginatedResponse<AffiliateRepresentation>>;
}

export async function getBloodTypes() {
  return request({
    url: `blood-types/`,
    method: 'GET'
  }) as AxiosPromise<BloodType[]>;
}

export async function getUserPKIS() {
  return request({
    url: `users/me/pkis/`,
    method: 'GET',
  }) as AxiosPromise<UserPKIsResponse>;
}

// Employee statistics

export async function getEmployeeFilesCreated(params: any) {
  return request({
    url: `employee-statistics/files-created/`,
    method: 'GET',
    params
  }) as AxiosPromise<UserEmployeeResponse>;
}

export async function getEmployeeFilesApproved(params: any) {
  return request({
    url: `employee-statistics/files-approved/`,
    method: 'GET',
    params
  }) as AxiosPromise<UserEmployeeResponse>;
}

export async function getEmployeeValidatedPayments(params: any) {
  return request({
    url: `employee-statistics/verified-payments/`,
    method: 'GET',
    params
  }) as AxiosPromise<UserEmployeeResponse>;
}

export async function getEmployeeFilesApprovedAmount(params: any) {
  return request({
    url: `employee-statistics/files-approved-amount/`,
    method: 'GET',
    params
  }) as AxiosPromise<ApprovedFileAmount[]>;
}

export async function safeRequest(
  config: AxiosRequestConfig
): Promise<null | UserErrorDetail> {
  try {
    await request(config); 
    return null; 
  } catch (err) {
    const error = err as AxiosError<UserErrorDetail>;

    if (error.response && error.response.data) {
      return error.response.data;
    }

    return { detail: 'Error desconocido' };
  }
}

export async function updateIsActiveUser(
  id: number,
  data: { activate: boolean }
): Promise<null | UserErrorDetail> {
  return safeRequest({
    url: `employee-activate/${id}/change-user-active/`,
    method: 'PATCH',
    data
  });
}

export async function forceChangeUserPassword(data: {}) {
  return request({
    url: 'users/force-change-password/',
    method: 'PUT',
    data
  }) as AxiosPromise<CurrentUser>;
}